// oneTimeDiscounts.js
//
// v3.7.8 — one-time (non-recurring) manual discount engine. The NRC analog of
// manualDiscounts.js. A rep can attach one or more discount lines to a quote's
// ONE-TIME charges (Managed IT / Voice / Bundle / Multi-Site). Unlike the MRR
// manual discount (a general line off the recurring), a one-time discount is
// TARGETED at a specific NRC category — Onboarding, Hardware, Activation, or
// Number Porting — so the rep can, e.g., knock $200 off porting services while
// leaving hardware untouched. Each line is a percentage of that category's
// charge or a flat $ amount, and carries a free-text note.
//
// Single source of truth (same contract as manualDiscounts.js): BOTH the live
// quote pages (on-screen net NRC) AND quoteTotals.buildTotals (persisted totals)
// call applyOneTimeDiscounts so the displayed number and the saved number can
// never drift. The signed-document renderers (proposalSections.js /
// buildSignatureDocument.js) do NOT recompute — they read the resolved lines +
// per-category nets straight from totals.
//
// Design decisions (locked with Shaun, 2026-08-04):
//   - Targeted per category (Shaun's choice over a single "off the whole NRC"
//     line): each line names the NRC bucket it reduces.
//   - Percentages resolve against the SAME per-category base (the post-onboarding-
//     incentive category charge), so line order never changes the result and each
//     line's $ impact is independently explainable on the contract.
//   - Each category's net is floored at 0 — a discount can never bleed across
//     categories (a $500 porting discount on $200 of porting does NOT eat into
//     hardware) and the customer one-time total can never go negative.
//   - No min-commitment floor — like the MRR manual discount this is a deliberate
//     override; the only guard is the GM warning surfaced on the quote page.

// Shape of an input line (lives on quotes.inputs.oneTimeDiscounts):
//   { category: 'onboarding'|'hardware'|'activation'|'porting',
//     type: 'pct'|'fixed', value: number, note: string }

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Canonical category set. `label` is the customer-facing wording used on the
// proposal / Service Order; `short` is the compact label for the editor card.
export const ONE_TIME_CATEGORIES = [
  { key: 'onboarding', label: 'Onboarding & Implementation', short: 'Onboarding' },
  { key: 'hardware',   label: 'Hardware',                    short: 'Hardware' },
  { key: 'activation', label: 'Activation & Provisioning',   short: 'Activation' },
  { key: 'porting',    label: 'Number Porting',              short: 'Porting' },
];

const CATEGORY_KEYS = ONE_TIME_CATEGORIES.map((c) => c.key);
const VALID_CATEGORY = new Set(CATEGORY_KEYS);

/** Human label for a category key (falls back to the raw key). */
export function oneTimeCategoryLabel(key) {
  const c = ONE_TIME_CATEGORIES.find((x) => x.key === key);
  return c ? c.label : String(key || '');
}

// Coerce a raw bases dict into the canonical { onboarding, hardware,
// activation, porting } shape with numeric, rounded values.
function normalizeBases(bases) {
  const b = bases || {};
  return {
    onboarding: round2(b.onboarding),
    hardware:   round2(b.hardware),
    activation: round2(b.activation),
    porting:    round2(b.porting),
  };
}

/**
 * Apply one-time discount lines to a set of per-category base charges.
 *
 * @param {object} bases — per-category customer charge BEFORE one-time
 *   discounts: { onboarding, hardware, activation, porting }. `onboarding`
 *   should already be net of any onboarding incentive (effectiveOnboarding).
 * @param {Array} discounts — raw input lines from quotes.inputs.oneTimeDiscounts.
 * @returns {{
 *   lines: Array,        // resolved [{ category, type, value, note, amount }]
 *   byCategory: object,  // { <key>: { base, discount, net } } for every category
 *   total: number,       // total $ removed (capped so nets never go negative)
 *   base: number,        // sum of all category bases (pre one-time discount)
 *   finalNRC: number,    // sum of per-category nets (customer one-time total)
 * }}
 */
export function applyOneTimeDiscounts(bases, discounts) {
  const b = normalizeBases(bases);
  const lines = [];
  const requested = { onboarding: 0, hardware: 0, activation: 0, porting: 0 };

  const list = Array.isArray(discounts) ? discounts : [];
  for (const d of list) {
    if (!d) continue;
    const category = VALID_CATEGORY.has(d.category) ? d.category : 'onboarding';
    const value = Number(d.value) || 0;
    if (value <= 0) continue;               // ignore blank/zero rows
    const type = d.type === 'pct' ? 'pct' : 'fixed';
    const catBase = b[category] || 0;
    // Percentage lines resolve against the category base, not the running
    // balance, so line order is irrelevant and each line is auditable.
    const amount = round2(type === 'pct' ? catBase * (value / 100) : value);
    if (amount <= 0) continue;
    lines.push({ category, type, value, note: String(d.note || '').trim(), amount });
    requested[category] += amount;
  }

  // Per-category netting with a floor at 0. `discount` is the EFFECTIVE
  // reduction (capped at the base); `net` is what the customer still pays.
  const byCategory = {};
  let total = 0;
  for (const key of CATEGORY_KEYS) {
    const base = b[key];
    const applied = Math.min(base, round2(requested[key]));
    const net = round2(base - applied);
    byCategory[key] = { base, discount: applied, net };
    total += applied;
  }

  total = round2(total);
  const base = round2(b.onboarding + b.hardware + b.activation + b.porting);
  const finalNRC = round2(base - total);
  return { lines, byCategory, total, base, finalNRC };
}

/**
 * True when a discount array has at least one effective (value > 0) line.
 * Cheap guard for the pages/renderers so they can skip the discount UI/section.
 */
export function hasOneTimeDiscounts(discounts) {
  return Array.isArray(discounts)
    && discounts.some((d) => d && (Number(d.value) || 0) > 0);
}

/**
 * Normalize an array for persistence: drop empty rows, coerce category/type,
 * trim notes. Returns [] when nothing effective remains.
 */
export function normalizeOneTimeDiscounts(discounts) {
  if (!Array.isArray(discounts)) return [];
  return discounts
    .filter((d) => d && (Number(d.value) || 0) > 0)
    .map((d) => ({
      category: VALID_CATEGORY.has(d.category) ? d.category : 'onboarding',
      type:     d.type === 'pct' ? 'pct' : 'fixed',
      value:    Number(d.value) || 0,
      note:     String(d.note || '').trim(),
    }));
}

/** Short human label for a line, e.g. "Porting 10%" or "Hardware $50". */
export function oneTimeDiscountLineLabel(line) {
  if (!line) return '';
  const c = ONE_TIME_CATEGORIES.find((x) => x.key === line.category);
  const cat = c ? c.short : (line.category || '');
  const amt = line.type === 'pct' ? `${line.value}%` : `$${line.value}`;
  return `${cat} ${amt}`.trim();
}
